import React, { useState, useEffect, useContext, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Row, Col } from "reactstrap";
import classnames from "classnames";
import { BreadcrumbContext } from "../../../../Layouts/Header";
import { VOLUNTEER_PATHS } from "Routes/paths";
import { useVolunteerStudyDashboardDetailsService } from "../api/useVolunteerStudyHistoryDetailsService";
import { formatDate } from "helpers/constant";
import FilePreviewModal from "Components/Common/FilePreviewModal";
import CRFScreeningModal from "modules/Volunteer/AssigningStudyModule/StudyScreening/components/CRFScreeningModal";
import TruncatedText from "Components/Common/TruncatedText";
import styles from "../css/volunteerstudydashboard.module.scss";
import FullPageLoader from "Components/Common/FullPageLoader";
import { extractVolunteerInfo, formatLabel, normalizeDashboardTabs, renderSectionData, tabConfigMap } from "../helper/volunteerstudydashboard.constant";

/* ================= COMPONENT ================= */
const VolunteerStudyDashboard: React.FC = () => {
  const { id: volunteerId, studyId } = useParams();
  const navigate = useNavigate();

  const breadcrumbContext = useContext(BreadcrumbContext);
  const setBreadcrumbItems = breadcrumbContext?.setBreadcrumbItems;

  const [activeStage, setActiveStage] = useState<number>(1);
  const [previewFile, setPreviewFile] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [activeSubTabs, setActiveSubTabs] = useState<Record<string, string>>({});
  const [selectedCRF, setSelectedCRF] = useState<any | null>(null);

  const onViewAttachment = (filePath: string) => {
    const baseUrl = localStorage.getItem("filebaseUrl");
    setPreviewFile(`${baseUrl}${filePath}`);
    setPreviewOpen(true);
  };

  // Update breadcrumb when CRF modal opens/closes
  useEffect(() => {
    if (selectedCRF) {
      const crfTitle = selectedCRF?.crfdetails?.title
        || selectedCRF?.selectedCrfDetails?.crfdetails?.title
        || "CRF View";
      setBreadcrumbItems?.([
        { text: "Volunteers", link: VOLUNTEER_PATHS.LIST },
        { text: "Volunteer Details", link: `/volunteer/details/${volunteerId}` },
        { text: "Study History", link: `/volunteer/details/${volunteerId}/study` },
        { text: "Volunteer Study Dashboard", onclick: () => setSelectedCRF(null) },
        { text: crfTitle },
      ]);
    } else {
      setBreadcrumbItems?.([
        { text: "Volunteers", link: VOLUNTEER_PATHS.LIST },
        { text: "Volunteer Details", link: `/volunteer/details/${volunteerId}` },
        { text: "Study History", link: `/volunteer/details/${volunteerId}/study` },
        { text: "Volunteer Study Dashboard" },
      ]);
    }
  }, [selectedCRF, volunteerId, studyId]);

  const { VolunteerStudyDashboardQuery } = useVolunteerStudyDashboardDetailsService({
    studyid: Number(studyId),
    volunteerid: Number(volunteerId),
  });

  const backendDashboard = useMemo(() => {
    const rawData = VolunteerStudyDashboardQuery?.data?.data;
    if (Array.isArray(rawData)) {
      return rawData[0] || null;
    }
    return rawData || null;
  }, [VolunteerStudyDashboardQuery?.data?.data]);

  const tabs = useMemo(
    () => normalizeDashboardTabs(backendDashboard?.modules || backendDashboard?.tabs),
    [backendDashboard]
  );

  // Dynamically extract volunteer code and subject number from backend dashboard data
  const { volunteerCode, subjectNo } = useMemo(
    () => extractVolunteerInfo(backendDashboard),
    [backendDashboard]
  );

  const isLoading = VolunteerStudyDashboardQuery.isLoading || VolunteerStudyDashboardQuery.isFetching;

  /* -----------------------------
     Dynamic Timeline Date Calculator
  ----------------------------- */
  const timelineDates = useMemo(() => {
    const regDate = tabs?.registration?.tabInfo?.performedDate;
    const asnDate = tabs?.assigned?.tabInfo?.performedDate;
    const scrDate = tabs?.screening?.tabInfo?.performedDate;
    const enrDate = tabs?.enrollment?.tabInfo?.performedDate;
    const cmpDate = tabs?.ambulatoryVisit?.tabInfo?.performedDate;

    return {
      reg: formatDate(regDate),
      asn: formatDate(asnDate),
      scr: formatDate(scrDate),
      enr: formatDate(enrDate),
      cmp: formatDate(cmpDate),
    };
  }, [tabs]);

  const timelineSteps = useMemo(() => {
    if (!tabs) {
      return [
        { id: 1, key: "registration", label: "Registration", icon: "ri-user-line", date: String(timelineDates.reg || "—"), status: "COMPLETED", classType: "green", enabled: true },
        { id: 2, key: "assigned", label: "Assigned", icon: "ri-file-text-line", date: String(timelineDates.asn || "—"), status: "COMPLETED", classType: "green", enabled: true },
        { id: 3, key: "screening", label: "Screening", icon: "ri-pulse-line", date: String(timelineDates.scr || "—"), status: "PASSED", classType: "green", enabled: true },
        { id: 4, key: "enrollment", label: "Enrollment", icon: "ri-shield-user-line", date: String(timelineDates.enr || "—"), status: "COMPLETED", classType: "green", enabled: true },
        { id: 5, key: "periods", label: "Periods", icon: "ri-calendar-2-line", date: "—", status: "PENDING", classType: "grey", enabled: true },
        { id: 6, key: "ambulatoryVisit", label: "Ambulatory Visit", icon: "ri-walk-line", date: "—", status: "PENDING", classType: "grey", enabled: true },
        { id: 7, key: "adverseEvent", label: "Adverse Event", icon: "ri-error-warning-line", date: "—", status: "PENDING", classType: "grey", enabled: true },
        { id: 8, key: "dropOut", label: "Drop Out", icon: "ri-logout-box-r-line", date: "—", status: "PENDING", classType: "grey", enabled: true },
        { id: 9, key: "postblock", label: "Post Block", icon: "ri-lock-line", date: "—", status: "PENDING", classType: "grey", enabled: true },
        { id: 10, key: "completion", label: "Study Completed", icon: "ri-award-line", date: "—", status: "PENDING", classType: "grey", enabled: true },
      ];
    }

    const steps: any[] = [];
    let idCounter = 1;

    const keyOrder = [
      "registration",
      "assigned",
      "screening",
      "transfer",
      "enrollment",
      "periods",
      "ambulatoryVisit",
      "adverseEvent",
      "dropOut",
      "postblock",
      "completion",
    ];

    // Find all keys in tabs
    const allTabKeys = tabs ? Object.keys(tabs) : [];
    const hasDynamicPeriods = allTabKeys.some((k) => k.toLowerCase().startsWith("period_"));

    // Union of keyOrder and any extra keys in tabs
    const mergedKeys = [...keyOrder];
    allTabKeys.forEach((k) => {
      if (!mergedKeys.includes(k)) {
        if (k.toLowerCase().startsWith("period_")) {
          // Dynamic period tabs should be placed where 'periods' is
          const periodsIdx = mergedKeys.indexOf("periods");
          if (periodsIdx !== -1) {
            mergedKeys.splice(periodsIdx, 0, k);
          } else {
            const ambIdx = mergedKeys.indexOf("ambulatoryVisit");
            if (ambIdx !== -1) {
              mergedKeys.splice(ambIdx, 0, k);
            } else {
              mergedKeys.push(k);
            }
          }
        } else {
          // Insert other extra keys before 'completion' (which is usually the last item)
          const completionIdx = mergedKeys.indexOf("completion");
          if (completionIdx !== -1) {
            mergedKeys.splice(completionIdx, 0, k);
          } else {
            mergedKeys.push(k);
          }
        }
      }
    });

    mergedKeys.forEach((key) => {
      const tabData = tabs?.[key];
      const isRequired = [
        "registration",
        "assigned",
        "screening",
        "enrollment",
        ...(hasDynamicPeriods ? [] : ["periods"]),
        "ambulatoryVisit",
        "adverseEvent",
        "dropOut",
        "postblock",
        "completion"
      ].includes(key);
      const hasTab = tabData !== undefined;

      // Only display if tab is present, or if it is required
      if (hasTab || isRequired) {
        // Skip if tabInfo explicitly sets visible to false
        if (tabData) {
          if (tabData.tabInfo && tabData.tabInfo.visible === false) {
            return;
          }
        } else {
          // If tab did not come from backend and tabs data is loaded,
          // hide optional/completion stages to avoid displaying empty required fallback tabs
          if (["adverseEvent", "dropOut", "postblock", "completion"].includes(key)) {
            return;
          }
        }

        let config = tabConfigMap[key];
        if (!config) {
          let icon = "ri-information-line";
          let classType = "grey";
          const lowerKey = key.toLowerCase();
          if (lowerKey.includes("checkin")) {
            icon = "ri-login-box-line";
            classType = "blue";
          } else if (lowerKey.includes("dosing")) {
            icon = "ri-capsule-line";
            classType = "blue";
          } else if (lowerKey.includes("checkout")) {
            icon = "ri-logout-box-line";
            classType = "blue";
          }
          config = {
            label: tabData?.tabInfo?.title || formatLabel(key),
            icon,
            classType,
          };
        }

        let dateValue = "—";
        let statusValue = "PENDING";
        let isTabCompleted = false;
        let isEnabled = true;

        if (tabData?.tabInfo) {
          isTabCompleted = !!tabData.tabInfo.completed;
          statusValue = (tabData.tabInfo.status || (isTabCompleted ? "COMPLETED" : "PENDING")).toUpperCase();
          dateValue = tabData.tabInfo.performedDate ? formatDate(tabData.tabInfo.performedDate) : "—";
          isEnabled = tabData.tabInfo.enabled !== false;
        } else {
          if (key === "registration") {
            isTabCompleted = true;
            statusValue = "COMPLETED";
            dateValue = String(timelineDates.reg || "—");
          } else if (key === "assigned") {
            isTabCompleted = true;
            statusValue = "COMPLETED";
            dateValue = String(timelineDates.asn || "—");
          } else if (key === "screening") {
            isTabCompleted = true;
            statusValue = "PASSED";
            dateValue = String(timelineDates.scr || "—");
          } else if (key === "enrollment") {
            isTabCompleted = true;
            statusValue = "COMPLETED";
            dateValue = String(timelineDates.enr || "—");
          }
        }

        if (key === "periods" && tabData) {
          const periodsList =
            tabData.periods ||
            tabData.data?.periods ||
            tabData.data?.["Period Details"] ||
            tabData.data?.perioddetails ||
            [];
          const completedCount =
            periodsList.filter(
              (p: any) =>
                p.status === "Completed" || p.completed === true || p.status?.toLowerCase() === "completed"
            ).length || 0;
          const totalCount = periodsList.length || 0;
          if (totalCount > 0) {
            dateValue = `${completedCount} / ${totalCount} Completed`;
          }
        } else if (key === "ambulatoryVisit" && tabData) {
          const visitsList =
            tabData.visits ||
            tabData.data?.Visits ||
            tabData.data?.visits ||
            tabData.data?.visitsdetails ||
            [];
          const completedCount =
            visitsList.filter((v: any) => {
              const info = v?.["Visit Information"] || v;
              const status = info?.["Visit Status"] || info?.status || "";
              return status.toLowerCase() === "completed" || info?.completed === true;
            }).length || 0;
          const totalCount = visitsList.length || 0;
          if (totalCount > 0) {
            dateValue = `${completedCount} / ${totalCount} Completed`;
          }
        } else if (key === "completion") {
          if (tabData?.tabInfo) {
            isTabCompleted = !!tabData.tabInfo.completed;
            statusValue = (tabData.tabInfo.status || (isTabCompleted ? "COMPLETED" : "PENDING")).toUpperCase();
            dateValue = tabData.tabInfo.performedDate ? formatDate(tabData.tabInfo.performedDate) : "—";
          } else {
            isTabCompleted = !!tabs?.ambulatoryVisit?.tabInfo?.completed;
            statusValue = isTabCompleted ? "COMPLETED" : "PENDING";
            dateValue = isTabCompleted ? String(timelineDates.cmp || "—") : "—";
          }
        }

        // Determine step visual state: green (completed), blue (active/in-progress), grey (pending)
        let classType = "grey";
        if (statusValue === "COMPLETED" || statusValue === "PASSED" || statusValue === "ASSIGNED") {
          classType = "green";
        } else if (statusValue === "IN PROGRESS" || statusValue === "SCHEDULED" || activeStage === idCounter) {
          classType = "blue";
        }

        steps.push({
          id: idCounter++,
          key,
          label: tabData?.tabInfo?.title || config.label,
          icon: config.icon,
          date: dateValue,
          status: statusValue,
          classType,
          enabled: isEnabled,
        });
      }
    });

    return steps;
  }, [tabs, timelineDates, activeStage]);
  const activeStep = timelineSteps.find(step => step.id === activeStage);
  const activeKey = activeStep?.key || "registration";

  if (isLoading) {
    return (
      <FullPageLoader />
    );
  }

  return (
    <Container fluid>
      <Row className="g-3">
        {/* ================= LEFT SIDEBAR (STEPPER) ================= */}
        <Col xl={3} lg={4} md={12}>
          {/* Modern Stepper Card */}
          <div className={styles.lifecycleCard}>
            <div className={styles.cardHeader}>
              <div className={styles.headerIcon}>
                <i className="ri-road-map-line" />
              </div>
              <div className={styles.headerText}>
                <h5>Study Lifecycle</h5>
                <p>Track the volunteer progress</p>
              </div>
            </div>
            <div className={styles.verticalStepper}>
              {timelineSteps.map((step, index) => (
                <div
                  key={step.id}
                  className={classnames(
                    styles.verticalStep,
                    styles[step.classType],
                    activeStage === step.id ? styles.activeStep : "",
                    step.enabled === false ? "opacity-50 pe-none" : "cursor-pointer"
                  )}
                  onClick={() => step.enabled !== false && setActiveStage(step.id)}
                >
                  {index < timelineSteps.length - 1 && (
                    <div
                      className={classnames(
                        styles.stepConnector,
                        step.classType === "green" ? styles.completedConnector :
                          step.classType === "blue" ? styles.activeConnector : ""
                      )}
                    />
                  )}
                  <div className={styles.circleContainer}>
                    <div className={classnames(
                      styles.statusDot,
                      step.classType === "green" ? styles.completed :
                        step.classType === "blue" ? styles.active :
                          styles.pending
                    )}>
                      {step.classType === "green" && <i className="ri-check-line" />}
                    </div>
                  </div>

                  <div
                    className={classnames(
                      styles.stepCard,
                      activeStage === step.id ? styles.activeStepCard : ""
                    )}
                  >
                    <div className={classnames(
                      styles.cardIconContainer,
                      step.classType === "green" ? styles.completedIcon :
                        step.classType === "blue" ? styles.activeIcon :
                          ""
                    )}>
                      <i className={step.icon} />
                    </div>

                    <div className={styles.stepInfo}>
                      <div className="d-flex align-items-center gap-1 w-100" style={{ minWidth: 0 }}>
                        <span
                          className={styles.stepNum}
                          style={{
                            color: step.classType === "green" ? "#10b981" : step.classType === "blue" ? "#3b82f6" : "#94a3b8"
                          }}
                        >
                          {String(step.id).padStart(2, "0")}
                        </span>
                        <span className={styles.label}>
                          <TruncatedText text={step.label} maxWidth="100%" />
                        </span>
                      </div>
                      <span className={styles.date}>
                        <i className="ri-calendar-line me-1" style={{ fontSize: "10px" }} />
                        <TruncatedText text={step.date} maxWidth="100%" />
                      </span>
                      <div>
                        <span className={classnames(
                          styles.statusBadgeText,
                          step.classType === "green" ? styles.completedBadge :
                            step.classType === "blue" ? styles.inProgressBadge :
                              styles.pendingBadge
                        )}>
                          {step.status}
                        </span>
                      </div>
                    </div>

                    <div className={styles.chevronWrapper}>
                      <i className="ri-arrow-right-s-line" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Col>

        {/* ================= RIGHT MAIN CONTENT AREA ================= */}
        <Col xl={9} lg={8} md={12}>
          <div className={styles.stageContentCard}>
            {activeStep ? (
              <div className={classnames("d-flex flex-column h-100 overflow-hidden", styles.stageTransition)}>
                <div className={styles.stageHeader}>
                  <div className={styles.titleSection}>
                    <h4>
                      <i className={classnames(activeStep.icon, `text-${activeStep.classType}`)} />{" "}
                      {activeStep.label} Details
                    </h4>
                    <p>
                      {tabs?.[activeKey]?.tabInfo?.status ? `Status: ${tabs[activeKey].tabInfo.status}` : "Status: Pending"}
                      {tabs?.[activeKey]?.tabInfo?.performedBy && ` | Performed By: ${tabs[activeKey].tabInfo.performedBy}`}
                      {tabs?.[activeKey]?.tabInfo?.performedDate && ` | Date: ${formatDate(tabs[activeKey].tabInfo.performedDate)}`}
                    </p>
                  </div>

                  {/* Right Side: Global Volunteer & Subject Info */}
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    {volunteerCode && (
                      <div
                        className="d-flex align-items-center gap-2 px-3 py-1.5 rounded shadow-sm border bg-white"
                        style={{
                          borderColor: "#cbd5e1",
                          fontSize: "0.85rem",
                          fontWeight: 600,
                          color: "#1e293b",
                          minHeight: "38px"
                        }}
                      >
                        <i className="ri-user-line text-primary" style={{ fontSize: "1.05rem" }} />
                        <div className="d-flex flex-column text-start" style={{ lineHeight: 1.2 }}>
                          <span className="text-secondary" style={{ fontSize: "0.625rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}>Volunteer Code</span>
                          <span className="text-dark fw-bold">{volunteerCode}</span>
                        </div>
                      </div>
                    )}
                    {subjectNo && (
                      <div
                        className="d-flex align-items-center gap-2 px-3 py-1.5 rounded shadow-sm border bg-white"
                        style={{
                          borderColor: "#cbd5e1",
                          fontSize: "0.85rem",
                          fontWeight: 600,
                          color: "#1e293b",
                          minHeight: "38px"
                        }}
                      >
                        <i className="ri-shield-user-line text-success" style={{ fontSize: "1.05rem" }} />
                        <div className="d-flex flex-column text-start" style={{ lineHeight: 1.2 }}>
                          <span className="text-secondary" style={{ fontSize: "0.625rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}>Subject No</span>
                          <span className="text-success fw-bold">
                            {subjectNo}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.stageBody}>
                  {tabs?.[activeKey]?.data ? (
                    // Check if data is a flat object of primitives, or has multiple sections
                    Object.values(tabs[activeKey].data).some((val) => val !== null && typeof val !== "object") ? (
                      <div className={styles.cardSection}>
                        <div className={styles.sectionTitle}>
                          <i className="ri-information-line" /> {activeStep.label} Summary
                        </div>
                        {renderSectionData(activeStep.label, tabs[activeKey].data, navigate, setSelectedCRF, onViewAttachment, activeSubTabs, setActiveSubTabs)}
                      </div>
                    ) : (
                      Object.entries(tabs[activeKey].data).map(([sectionKey, sectionVal]: [string, any]) => (
                        <div className={styles.cardSection} key={sectionKey}>
                          <div className={styles.sectionTitle}>
                            <i className="ri-folder-open-line" /> {formatLabel(sectionKey)}
                          </div>
                          {renderSectionData(sectionKey, sectionVal, navigate, setSelectedCRF, onViewAttachment, activeSubTabs, setActiveSubTabs)}
                        </div>
                      ))
                    )
                  ) : (
                    <div className="text-muted p-4 text-center">
                      <i className="ri-inbox-line fs-24 mb-2 d-block" />
                      No dashboard details available for this stage.
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="d-flex justify-content-center align-items-center h-100 text-muted">
                Select a stage from the lifecycle to view details.
              </div>
            )}
          </div>
        </Col>
      </Row>

      {/* File Preview Modal */}
      <FilePreviewModal
        fileUrl={previewFile}
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
      />

      {/* CRF MODAL */}
      <CRFScreeningModal
        isOpen={!!selectedCRF}
        toggle={() => setSelectedCRF(null)}
        selectedCrf={
          selectedCRF
            ? {
              id: selectedCRF.id || 0,
              json: selectedCRF.crfdetails || selectedCRF.selectedCrfDetails?.crfdetails,
              crfid: selectedCRF.crfid || selectedCRF.crfId || 0,
              crfdetails: selectedCRF.crfdetails || selectedCRF.selectedCrfDetails?.crfdetails,
              selectedCrfDetails: selectedCRF
            }
            : null
        }
        onClose={() => setSelectedCRF(null)}
        onSave={() => { }}
        isPending={false}
        readonly={true}
      />
    </Container>
  );
};

export default VolunteerStudyDashboard;